/**
 * "Assign new M365 license" billing resolution.
 *
 * The `m365_assign_license` ticket type bulk-assigns an M365 license to N users
 * at once. This module is the single brain shared by:
 *   - the `assignLicense` pricing rule (the ticket's current-period catch-up line), and
 *   - TicketEntryPage's save-time schedule sync (the recurring schedule mutation).
 * Both must agree on term, SKU, quantity, and the target schedule, so the
 * decision lives here rather than being duplicated. Modelled on
 * {@link resolveEolp2Billing}, but with a technician-chosen SKU, a quantity of
 * N (the license count), and — unlike EOLP2 — it never blocks: when the
 * customer has no matching schedule it *creates* one.
 *
 * Proration matrix (confirmed with the business):
 *   - Monthly license → not pro-rated; charge the standard monthly rate.
 *   - Annual license, customer has NO existing annual M365 subscription (any
 *     type) → charge the FULL YEAR now, and create a new annual schedule.
 *   - Annual license, customer HAS an existing annual M365 subscription
 *     renewing on D → pro-rate now→D to co-terminate, and add/increment the
 *     line on that existing annual schedule (soonest-renewing if several).
 * The recurring schedule mutation always happens; only the ticket's per-unit
 * amount varies. Quantity on both the ticket line and the schedule line is the
 * license count.
 */
import type { RenewalScheduleItem } from '@techgitter/contracts';
import { getProductBySku } from '../hooks/useProductCatalog';
import { skuMatchesCategory } from '../data/licenseCategories';

/** Count of non-empty (trimmed) lines — how License Count derives from Username(s). */
export function countUsernameLines(value: unknown): number {
  if (typeof value !== 'string') return 0;
  return value.split('\n').map((l) => l.trim()).filter(Boolean).length;
}

export type AssignLicenseTerm = 'monthly' | 'annual';

/** What the save-time sync should do to the recurring schedule. */
export type AssignLicenseSchedulePlan =
  /** Bump the existing line for this SKU on `scheduleId` by the count. */
  | { action: 'increment'; scheduleId: string }
  /** Add a new line for this SKU to the existing `scheduleId`. */
  | { action: 'add'; scheduleId: string }
  /** No suitable schedule exists — create one with this line. `frequency` uses
   *  the create-request enum ('Yearly', which the API maps to RS 'Annually'). */
  | { action: 'create'; frequency: 'Monthly' | 'Yearly'; nextRun: string };

export interface AssignLicenseBilling {
  sku: string;
  count: number;
  term: AssignLicenseTerm;
  /** Per-unit charge for THIS ticket's invoice (pro-rated / full month / full year). */
  ticketUnitCost: number;
  /** Ticket line quantity — the license count. */
  ticketQuantity: number;
  /** Full recurring per-period rate for the SKU (what each schedule unit bills). */
  recurringUnitCost: number;
  /** The recurring schedule mutation to perform on save. */
  schedule: AssignLicenseSchedulePlan;
  /** Optional blurb appended to the ticket line detail (e.g. proration note). */
  lineDetailAppend?: string;
  /** Human-readable pricing explanation for the ticket line item. */
  explanation: string;
}

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

/** ISO date (YYYY-MM-DD) for the 1st of `now`'s month, `years` years on. */
function firstOfMonthPlusYears(now: Date, years: number): string {
  const d = new Date(now.getFullYear() + years, now.getMonth(), 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-01`;
}

/** ISO date (YYYY-MM-DD) for the 1st of the month after `now`. */
function firstOfNextMonth(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-01`;
}

/** Earliest-renewing item wins when a customer has several annual schedules. */
function soonestRenewing(items: RenewalScheduleItem[]): RenewalScheduleItem {
  return items.reduce((best, i) =>
    new Date(i.nextRun).getTime() < new Date(best.nextRun).getTime() ? i : best,
  );
}

/**
 * Resolve the ticket line + recurring schedule mutation for assigning `count`
 * of `sku` to a customer, given their current renewal schedule. Returns null
 * when the SKU is unknown to the catalog (nothing to bill).
 */
export function resolveAssignLicenseBilling(
  schedule: RenewalScheduleItem[],
  now: Date,
  sku: string,
  count: number,
): AssignLicenseBilling | null {
  const product = getProductBySku(sku);
  if (!product) return null;

  const qty = Math.max(1, Math.floor(count) || 1);
  const displayName = product.displayName ?? sku;
  const rate = product.unitCost ?? 0;
  const term: AssignLicenseTerm = sku.endsWith('.Annual') ? 'annual' : 'monthly';

  // Active (unpaused) M365-family lines on the customer's schedules, by cadence.
  const m365 = schedule.filter(
    (i) => !i.paused && skuMatchesCategory(i.productSku, 'licensing-m365'),
  );
  const monthlyItems = m365.filter((i) => i.frequency.toLowerCase() === 'monthly');
  const annualItems = m365.filter((i) => i.frequency.toLowerCase() === 'annually');

  if (term === 'monthly') {
    // Monthly license: standard monthly rate, no proration. Target the monthly
    // schedule (increment the SKU line if present, else add it, else create).
    const existingLine = monthlyItems.find((i) => i.productSku === sku);
    let plan: AssignLicenseSchedulePlan;
    if (existingLine) {
      plan = { action: 'increment', scheduleId: existingLine.scheduleId };
    } else if (monthlyItems.length > 0) {
      plan = { action: 'add', scheduleId: soonestRenewing(monthlyItems).scheduleId };
    } else {
      plan = { action: 'create', frequency: 'Monthly', nextRun: firstOfNextMonth(now) };
    }
    return {
      sku, count: qty, term,
      ticketUnitCost: rate,
      ticketQuantity: qty,
      recurringUnitCost: rate,
      schedule: plan,
      explanation: `${displayName} — full month (monthly subscription, no proration)`,
    };
  }

  // Annual license. The unit cost of an annual line is the annual amount.
  const annualRate = sku.endsWith('.Annual') ? rate : rate * 12;

  if (annualItems.length === 0) {
    // No existing annual M365 subscription → full year now + create an annual
    // schedule that renews on the 1st of the current month in a year's time.
    return {
      sku, count: qty, term,
      ticketUnitCost: annualRate,
      ticketQuantity: qty,
      recurringUnitCost: annualRate,
      schedule: { action: 'create', frequency: 'Yearly', nextRun: firstOfMonthPlusYears(now, 1) },
      explanation: `${displayName} — full annual rate ($${annualRate.toFixed(2)}/yr), new subscription`,
    };
  }

  // Existing annual M365 subscription → co-terminate with it (soonest-renewing).
  const existingSkuLine = annualItems.find((i) => i.productSku === sku);
  const target = existingSkuLine ?? soonestRenewing(annualItems);
  const plan: AssignLicenseSchedulePlan = existingSkuLine
    ? { action: 'increment', scheduleId: existingSkuLine.scheduleId }
    : { action: 'add', scheduleId: target.scheduleId };

  const nextRun = new Date(target.nextRun);
  const monthsRemaining = monthsBetween(now, nextRun);

  if (monthsRemaining <= 0) {
    return {
      sku, count: qty, term,
      ticketUnitCost: annualRate,
      ticketQuantity: qty,
      recurringUnitCost: annualRate,
      schedule: plan,
      explanation: `${displayName} — full annual rate ($${annualRate.toFixed(2)}/yr)`,
    };
  }

  const prorated = Math.round(((annualRate * monthsRemaining) / 12) * 100) / 100;
  const dueMonth = nextRun.toLocaleString('en-AU', { month: 'long' });
  return {
    sku, count: qty, term,
    ticketUnitCost: prorated,
    ticketQuantity: qty,
    recurringUnitCost: annualRate,
    schedule: plan,
    lineDetailAppend: `pro-rated to co-incide with existing annual subscription due ${dueMonth}`,
    explanation: `${displayName} — prorated ${monthsRemaining}/12 months ($${annualRate.toFixed(2)}/yr)`,
  };
}
